import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { validateHighlightImage } from '../../utils/imageUtils';
import axios from 'axios';
import {
  ArrowLeftIcon,
  PhotoIcon,
  PlusIcon,
  TrashIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  UsersIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  SparklesIcon,
  CameraIcon,
  DocumentIcon,
  TagIcon,
  ClockIcon,
  BuildingOfficeIcon,
  EyeIcon,
  MapPinIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

// Normalize image URLs to work in dev (3000->5000) and prod
const resolveImageUrl = (img?: string) => {
  if (!img) return '';
  const normalized = String(img).replace(/\\/g, '/');
  // If absolute URL or data URL, return as-is
  if (/^(https?:)?\/\//i.test(normalized) || normalized.startsWith('data:')) {
    return normalized;
  }
  const idx = normalized.indexOf('/uploads/');
  const relPath = idx !== -1 ? normalized.slice(idx) : (normalized.startsWith('/') ? normalized : `/${normalized}`);
  const isDev = typeof window !== 'undefined' && window.location.port === '3000';
  const base = isDev ? 'http://localhost:5000' : '';
  return `${base}${relPath}`;
};

// ListSection component defined outside to prevent re-creation on renders
const ListSection = ({ 
  title, 
  items, 
  newItemKey, 
  listKey, 
  placeholder, 
  icon,
  newItems,
  setNewItems,
  addListItem,
  removeListItem 
}: { 
  title: string; 
  items: string[]; 
  newItemKey: string; 
  listKey: string; 
  placeholder: string;
  icon: string;
  newItems: any;
  setNewItems: any;
  addListItem: any;
  removeListItem: any;
}) => (
  <div className="bg-white/80 backdrop-blur rounded-2xl border border-orange-200/50 p-6 shadow-xl">
    <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
      {icon} {title}
    </h2>
    
    <div className="flex gap-2 mb-4">
      <input
        type="text"
        value={newItems[newItemKey]}
        onChange={(e) => setNewItems((prev: any) => ({ ...prev, [newItemKey]: e.target.value }))}
        placeholder={placeholder}
        className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addListItem(newItemKey, listKey))}
      />
      <button
        type="button"
        onClick={() => addListItem(newItemKey, listKey)}
        className="px-4 py-2 bg-yellow-500 text-white rounded-xl hover:bg-yellow-600 transition-colors"
      >
        <PlusIcon className="h-5 w-5" />
      </button>
    </div>
    
    <div className="flex flex-wrap gap-2">
      {items.map((item, index) => (
        <div key={index} className="inline-flex items-center gap-2 bg-yellow-50 text-yellow-900 border border-yellow-200 px-3 py-1.5 rounded-full shadow-sm">
          <span className="text-sm">{item}</span>
          <button
            type="button"
            onClick={() => removeListItem(listKey, index)}
            className="text-yellow-700 hover:text-red-600 hover:bg-white/60 rounded-full p-1 transition"
            title="Remove"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  </div>
);

interface Resort {
  _id: string;
  name: string;
  destination: { _id: string; name: string };
  bannerImage: string;
}

interface Destination {
  _id: string;
  name: string;
  image: string;
}

interface FormData {
  name: string;
  description: string;
  priceUSD: string;
  priceAUD: string;
  lastMinuteDealPriceUSD: string;
  lastMinuteDealPriceAUD: string;
  lastMinuteDealExpiryDate: string;
  validityPeriod: string;
  maxGuests: string;
  nights: string;
  packageCode: string;
  destination: string;
  resorts: string[];
  highlights: Array<{
    title: string;
    image?: File | null;
    imageUrl?: string;
  }>;
  inclusions: string[];
  exclusions: string[];
  premiumFeatures: string[];
  features: string[];
  termsAndConditions: string[];
  bannerImage: File | null;
  gallery: File[];
  isLive: boolean;
  // New action type fields
  actionType: 'enquire' | 'book';
  enquireEnabled: boolean;
  bookEnabled: boolean;
  resortSettings: Array<{
    resortId: string;
    showExploreOption: boolean;
  }>;
}

const CreatePackage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [availableResorts, setAvailableResorts] = useState<Resort[]>([]);
  const [availableDestinations, setAvailableDestinations] = useState<Destination[]>([]);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    priceUSD: '',
    priceAUD: '',
    lastMinuteDealPriceUSD: '',
    lastMinuteDealPriceAUD: '',
    lastMinuteDealExpiryDate: '',
    validityPeriod: '12',
    maxGuests: '2',
    nights: '3',
    packageCode: '',
    destination: '',
    resorts: [],
    highlights: [],
    inclusions: [],
    exclusions: [],
    premiumFeatures: [],
    features: [],
    termsAndConditions: [],
    bannerImage: null,
    gallery: [],
    isLive: false,
    // New action type fields
    actionType: 'book',
    enquireEnabled: false,
    bookEnabled: true,
    resortSettings: []
  });

  const [previewImages, setPreviewImages] = useState<{ banner?: string; gallery: string[] }>({ gallery: [] });
  const [newItems, setNewItems] = useState({
    highlight: { title: '', image: null as File | null },
    inclusion: '',
    exclusion: '',
    premiumFeature: '',
    feature: '',
    termCondition: ''
  });
  const [existingBanner, setExistingBanner] = useState<string>('');
  const [existingGallery, setExistingGallery] = useState<string[]>([]);
  const [quickDeleteMode, setQuickDeleteMode] = useState(false);
  const [bannerDragActive, setBannerDragActive] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editingHighlightIndex, setEditingHighlightIndex] = useState<number | null>(null);

  const isEditing = Boolean(id);

  useEffect(() => {
    fetchAvailableDestinations();
    fetchAvailableResorts();
    if (isEditing) {
      fetchPackageData();
    }
  }, [id]);

  // Filter resorts by destination
  const filteredResorts = React.useMemo(() => {
    if (!formData.destination) {
      return availableResorts;
    }
    
    // Enhanced filtering to handle different data structures
    const filtered = availableResorts.filter(resort => {
      // Handle both string and object destination formats
      const resortDestinationId = typeof resort.destination === 'string' 
        ? resort.destination 
        : resort.destination?._id;
      
      return resortDestinationId === formData.destination;
    });
    
    console.log('Filtering resorts:');
    console.log('- destination ID:', formData.destination);
    console.log('- Available resorts:', availableResorts.length);
    console.log('- Filtered resorts:', filtered.length);
    console.log('- Sample resort structure:', availableResorts[0]);
    
    return filtered;
  }, [availableResorts, formData.destination]);

  // Reset selected resorts when destination changes
  useEffect(() => {
    if (formData.destination && formData.resorts.length > 0) {
      // Check if current resorts belong to the selected destination
      const validResorts = formData.resorts.filter(resortId => 
        filteredResorts.some(resort => resort._id === resortId)
      );
      
      if (validResorts.length !== formData.resorts.length) {
        setFormData(prev => ({ ...prev, resorts: validResorts }));
      }
    }
  }, [formData.destination, filteredResorts]);

  const fetchAvailableDestinations = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await axios.get(`${apiUrl}/api/destinations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setAvailableDestinations(response.data.destinations || []);
    } catch (error) {
      console.error('Failed to fetch destinations:', error);
    }
  };

  const fetchAvailableResorts = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      // Try multiple endpoints for better compatibility
      const endpoints = [
        isEditing 
          ? `${apiUrl}/api/packages/${id}/available-resorts`
          : `${apiUrl}/api/packages/available-resorts/new`,
        `${apiUrl}/api/resorts`, // Fallback to general resorts endpoint
      ];

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      let resorts = [];
      let lastError = null;
      
      for (const endpoint of endpoints) {
        try {
          console.log('Trying endpoint:', endpoint);
          const response = await axios.get(endpoint, { 
            headers,
            withCredentials: true,
            timeout: 10000 // 10 second timeout
          });
          
          resorts = response.data.resorts || response.data || [];
          console.log('Successfully fetched resorts:', resorts.length);
          console.log('Sample resort structure:', resorts[0]);
          break;
        } catch (err) {
          console.log('Endpoint failed:', endpoint, err);
          lastError = err;
          continue;
        }
      }
      
      setAvailableResorts(resorts);
      
      if (resorts.length === 0) {
        console.warn('No resorts loaded - this may cause filtering issues');
      }
    } catch (error: any) {
      console.error('❌ Error fetching resorts:', error?.response?.data || error?.message || error);
      setError(`Failed to load resorts: ${error?.response?.data?.message || error?.message || 'Unknown error'}`);
      setAvailableResorts([]);
    }
  };

  // Drag-n-drop handlers for banner
  const onBannerDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setBannerDragActive(true);
  };
  const onBannerDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setBannerDragActive(false);
  };
  const onBannerDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setBannerDragActive(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;
    const file = files[0];
    if (!file.type.startsWith('image/')) return;
    setFormData(prev => ({ ...prev, bannerImage: file }));
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewImages(prev => ({ ...prev, banner: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  const removeBannerImage = () => {
    setFormData(prev => ({ ...prev, bannerImage: null }));
    setPreviewImages(prev => ({ ...prev, banner: existingBanner }));
  };

  const fetchPackageData = async () => {
    if (!id) return;
    
    console.log('🔍 Fetching package data for ID:', id);
    setLoading(true);
    setError('');
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      console.log('📡 API URL:', `${apiUrl}/api/packages/${id}`);
      
      const response = await axios.get(`${apiUrl}/api/packages/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });
      
      console.log('📦 Package response:', response.data);
      
      const pkg = response.data.package || response.data;
      
      if (!pkg) {
        console.error('❌ No package data received');
        setError('Package not found or could not be loaded');
        return;
      }
      
      console.log('✅ Setting form data with package:', pkg.name);
      
      setFormData({
        name: pkg.name || '',
        description: pkg.description || '',
        priceUSD: pkg.priceUSD?.toString() || '',
        priceAUD: pkg.priceAUD?.toString() || '',
        lastMinuteDealPriceUSD: pkg.lastMinuteDealPriceUSD?.toString() || '',
        lastMinuteDealPriceAUD: pkg.lastMinuteDealPriceAUD?.toString() || '',
        lastMinuteDealExpiryDate: pkg.lastMinuteDealExpiryDate ? new Date(pkg.lastMinuteDealExpiryDate).toISOString().split('T')[0] : '',
        validityPeriod: pkg.validityPeriod?.toString() || '12',
        maxGuests: pkg.maxGuests?.toString() || '2',
        nights: pkg.nights?.toString() || '3',
        packageCode: pkg.packageCode || '',
        destination: pkg.destination?._id || pkg.destination || '',
        resorts: pkg.resorts?.map((r: any) => r._id || r) || [],
        // Load highlights with resolved imageUrls
        highlights: (pkg.highlights || []).map((h: any) => {
          console.log('📌 Loading highlight:', h);
          return {
            title: h.title || (typeof h === 'string' ? h : ''),
            imageUrl: h.imageUrl ? resolveImageUrl(h.imageUrl) : undefined,
            image: null
          };
        }),
        inclusions: pkg.inclusions || [],
        exclusions: pkg.exclusions || [],
        premiumFeatures: pkg.premiumFeatures || [],
        features: pkg.features || [],
        termsAndConditions: pkg.termsAndConditions || [],
        bannerImage: null,
        gallery: [],
        isLive: pkg.isLive || false,
        // New action type fields
        actionType: pkg.actionType || 'book',
        enquireEnabled: pkg.enquireEnabled || false,
        bookEnabled: pkg.bookEnabled !== false, // Default to true if not specified
        resortSettings: pkg.resortSettings || []
      });
      
      // Load existing banner image
      if (pkg.bannerImage) {
        const resolved = resolveImageUrl(pkg.bannerImage);
        setExistingBanner(resolved);
        setPreviewImages(prev => ({ ...prev, banner: resolved }));
      }
      
      // Load existing gallery images
      if (pkg.gallery && Array.isArray(pkg.gallery) && pkg.gallery.length > 0) {
        console.log('🇼️ Loading existing gallery images:', pkg.gallery.length);
        const resolvedGallery = pkg.gallery.map((img: string) => resolveImageUrl(img));
        setExistingGallery(resolvedGallery);
        setPreviewImages(prev => ({ ...prev, gallery: resolvedGallery }));
        console.log('✅ Gallery images loaded:', resolvedGallery.length);
      }
      
      console.log('✅ Package data loaded successfully');
    } catch (error: any) {
      console.error('❌ Error fetching package:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      
      if (error.response?.status === 404) {
        setError('Package not found. It may have been deleted.');
        setTimeout(() => navigate('/admin/packages'), 3000);
      } else if (error.response?.status === 401 && !error.response?.data?.preserveSession) {
        setError('Authentication failed. Please log in again.');
        // Let the axios interceptor handle the actual logout
      } else if (error.response?.status === 503) {
        setError('Database connection issue. Please try again in a moment.');
        // Retry after a delay
        setTimeout(() => {
          setError('');
          fetchPackageData();
        }, 3000);
      } else {
        setError(`Error loading package: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-generate package code from name
    if (name === 'name') {
      const code = value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 6);
      setFormData(prev => ({ ...prev, packageCode: code }));
    }
  };

  const handleResortSelection = (resortId: string) => {
    setFormData(prev => ({
      ...prev,
      resorts: prev.resorts.includes(resortId)
        ? prev.resorts.filter(id => id !== resortId)
        : [...prev.resorts, resortId]
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'banner' | 'gallery') => {
    const files = e.target.files;
    if (!files) return;

    if (type === 'banner') {
      const file = files[0];
      setFormData(prev => ({ ...prev, bannerImage: file }));
      
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewImages(prev => ({ ...prev, banner: reader.result as string }));
      };
      reader.readAsDataURL(file);
    } else {
      const fileArray = Array.from(files);
      setFormData(prev => ({ ...prev, gallery: [...prev.gallery, ...fileArray] }));
      
      fileArray.forEach(file => {
        const reader = new FileReader();
        reader.onload = () => {
          setPreviewImages(prev => ({ 
            ...prev, 
            gallery: [...prev.gallery, reader.result as string] 
          }));
        };
        reader.readAsDataURL(file);
      });
    }
  };

  // Helper function to add items to lists
  const addListItem = (field: keyof typeof newItems, listField: keyof FormData) => {
    // Handle highlight specially since it's now an object
    if (field === 'highlight') {
      const highlight = newItems[field];
      if (highlight.title.trim()) {
        setFormData(prev => ({
          ...prev,
          highlights: [...prev.highlights, {
            title: highlight.title.trim(),
            image: highlight.image,
            imageUrl: highlight.image ? URL.createObjectURL(highlight.image) : undefined
          }]
        }));
        setNewItems(prev => ({ ...prev, highlight: { title: '', image: null } }));
      }
      return;
    }
    
    // Handle string fields normally
    const value = (newItems[field] as string).trim();
    if (value) {
      setFormData(prev => ({
        ...prev,
        [listField]: [...(prev[listField] as string[]), value]
      }));
      setNewItems(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Helper function to remove items from lists
  const removeListItem = (listField: keyof FormData, index: number) => {
    setFormData(prev => ({
      ...prev,
      [listField]: (prev[listField] as any[]).filter((_, i) => i !== index)
    }));
  };

  // Helper functions for editing highlights
  const startEditingHighlight = (index: number) => {
    const highlight = formData.highlights[index];
    setEditingHighlightIndex(index);
    setNewItems(prev => ({
      ...prev,
      highlight: {
        title: highlight.title,
        image: null // Don't pre-populate the file input
      }
    }));
  };

  const saveHighlightEdit = () => {
    if (editingHighlightIndex !== null && newItems.highlight.title.trim()) {
      setFormData(prev => ({
        ...prev,
        highlights: prev.highlights.map((h, i) => {
          if (i === editingHighlightIndex) {
            return {
              title: newItems.highlight.title.trim(),
              image: newItems.highlight.image || h.image,
              imageUrl: newItems.highlight.image 
                ? URL.createObjectURL(newItems.highlight.image) 
                : h.imageUrl
            };
          }
          return h;
        })
      }));
      setEditingHighlightIndex(null);
      setNewItems(prev => ({ ...prev, highlight: { title: '', image: null } }));
    }
  };

  const cancelHighlightEdit = () => {
    setEditingHighlightIndex(null);
    setNewItems(prev => ({ ...prev, highlight: { title: '', image: null } }));
  };

  const removeGalleryImage = (index: number) => {
    const isExisting = index < existingGallery.length;
    const imageType = isExisting ? 'existing' : 'new';
    
    // Show confirmation dialog (unless in quick delete mode)
    if (!quickDeleteMode) {
      const confirmed = window.confirm(
        `Are you sure you want to delete this ${imageType} gallery image?\n\n` +
        `This action cannot be undone.`
      );
      
      if (!confirmed) return;
    }
    
    console.log(`🗑️ Removing ${imageType} gallery image at index:`, index);
    
    // Remove from preview images
    setPreviewImages(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index)
    }));
    
    // If it's an existing image, also remove from existingGallery
    if (isExisting) {
      console.log('🗑️ Removing existing gallery image');
      setExistingGallery(prev => prev.filter((_, i) => i !== index));
    } else {
      // Remove from form data (new images)
      const newImageIndex = index - existingGallery.length;
      setFormData(prev => ({
        ...prev,
        gallery: prev.gallery.filter((_, i) => i !== newImageIndex)
      }));
      console.log('🗑️ Removed new gallery image at index:', newImageIndex);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent rapid consecutive submissions
    if (loading) {
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      // Client-side validation
      if (!isEditing && !formData.bannerImage) {
        alert('Please upload a banner image');
        document.getElementById('images')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setLoading(false);
        return;
      }
      if (formData.bannerImage && formData.bannerImage.size > 5 * 1024 * 1024) {
        alert('Banner image must be less than 5MB');
        document.getElementById('images')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setLoading(false);
        return;
      }

      // Validate highlight images size with better utility
      for (const highlight of formData.highlights) {
        if (highlight.image) {
          const validation = validateHighlightImage(highlight.image);
          if (!validation.valid) {
            alert(`Highlight "${highlight.title}": ${validation.error}`);
            document.getElementById('highlights')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setLoading(false);
            return;
          }
        }
      }

      const submitData = new FormData();
      
      // Add package name first for directory naming
      submitData.append('name', formData.name);
      submitData.append('description', formData.description);
      
      // Convert numeric fields to proper format
      if (formData.priceUSD) submitData.append('priceUSD', parseFloat(formData.priceUSD).toString());
      if (formData.priceAUD) submitData.append('priceAUD', parseFloat(formData.priceAUD).toString());
      
      // Last minute deal pricing (optional)
      if (formData.lastMinuteDealPriceUSD) submitData.append('lastMinuteDealPriceUSD', parseFloat(formData.lastMinuteDealPriceUSD).toString());
      if (formData.lastMinuteDealPriceAUD) submitData.append('lastMinuteDealPriceAUD', parseFloat(formData.lastMinuteDealPriceAUD).toString());
      if (formData.lastMinuteDealExpiryDate) submitData.append('lastMinuteDealExpiryDate', formData.lastMinuteDealExpiryDate);
      
      if (formData.validityPeriod) submitData.append('validityPeriod', parseInt(formData.validityPeriod).toString());
      if (formData.maxGuests) submitData.append('maxGuests', parseInt(formData.maxGuests).toString());
      if (formData.nights) submitData.append('nights', parseInt(formData.nights).toString());
      
      submitData.append('packageCode', formData.packageCode);
      submitData.append('destination', formData.destination);
      submitData.append('isLive', formData.isLive.toString());
      
      // Add new action type fields
      submitData.append('actionType', formData.actionType);
      submitData.append('enquireEnabled', formData.enquireEnabled.toString());
      submitData.append('bookEnabled', formData.bookEnabled.toString());
      submitData.append('resortSettings', JSON.stringify(formData.resortSettings));
      
      // Add resorts
      formData.resorts.forEach(resortId => {
        submitData.append('resorts', resortId);
      });
      
      // Add highlights as JSON (cleaner approach)
      if (formData.highlights.length > 0) {
        // Filter out empty or invalid highlights before sending
        const validHighlights = formData.highlights.filter(h => 
          h.title && h.title.trim() !== '' && h.title.toLowerCase() !== 'undefined'
        );
        
        if (validHighlights.length > 0) {
          submitData.append('highlights', JSON.stringify(validHighlights));
          
          // Add highlight images separately
          validHighlights.forEach((highlight, index) => {
            if (highlight.image) {
              submitData.append(`highlightImages`, highlight.image);
              submitData.append(`highlightImageIndexes`, index.toString());
            }
          });
          
          console.log('🎯 Sending valid highlights:', validHighlights.length);
        } else {
          console.log('⚠️ No valid highlights to send');
        }
      }
      formData.inclusions.forEach(item => submitData.append('inclusions', item));
      formData.exclusions.forEach(item => submitData.append('exclusions', item));
      // Add premium features as simple strings (now simple)
      formData.premiumFeatures.forEach(item => submitData.append('premiumFeatures', item));
      formData.features.forEach(item => submitData.append('features', item));
      formData.termsAndConditions.forEach(item => submitData.append('termsAndConditions', item));

      // Add banner image
      if (formData.bannerImage) {
        submitData.append('bannerImage', formData.bannerImage);
      }
      
      // Add gallery images (new files)
      formData.gallery.forEach((file) => {
        submitData.append('gallery', file);
      });
      
      // Add existing gallery images that should be preserved
      if (isEditing && existingGallery.length > 0) {
        console.log('🗜️ Preserving existing gallery images:', existingGallery.length);
        existingGallery.forEach((imageUrl, index) => {
          submitData.append(`existingGallery[${index}]`, imageUrl);
        });
      }

      const url = isEditing 
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/packages/${id}`
        : `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/packages`;
      
      const method = isEditing ? 'PUT' : 'POST';

      console.log('Submitting package data...');
      console.log('Method:', method);
      console.log('URL:', url);
      console.log('Form data entries:');
      const entries = Array.from(submitData.entries());
      entries.forEach(([key, value]) => {
        console.log(`${key}:`, value);
      });

      const response = await axios({
        method,
        url,
        data: submitData,
        // Don't set Content-Type - let axios handle FormData boundary
        timeout: 70000, // 70 seconds timeout (slightly longer than server)
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
            console.log(`Upload Progress: ${percentCompleted}%`);
          }
        }
      });

      console.log('Package saved successfully:', response.data);
      setIsSubmitting(false);
      navigate('/admin/packages');
    } catch (error: any) {
      console.error('Error saving package:', error);
      
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        console.error('Full error response:', JSON.stringify(error.response.data, null, 2));
        
        if (error.response.status === 401 && !error.response.data?.preserveSession) {
          setError('Session expired. Please login again.');
          // The axios interceptor will handle the logout
        } else if (error.response.status === 401 && error.response.data?.preserveSession) {
          // Server error but session preserved
          setError(error.response.data?.message || 'An error occurred, but your session is still active.');
        } else if (error.response.status === 429) {
          setError('Too many requests. Please wait a moment before saving again.');
        } else if (error.response.status === 503) {
          setError('Database connection issue. Please try again in a moment.');
        } else if (error.response.status === 400) {
          // Show detailed validation errors
          const errorMsg = error.response.data?.message || 'Validation error';
          const errors = error.response.data?.errors;
          const details = error.response.data?.details;
          
          if (errors && Array.isArray(errors)) {
            console.error('Validation errors:', errors);
            setError(`${errorMsg}\n\nDetails:\n• ${errors.map(e => e.msg || e.message || e).join('\n• ')}`);
          } else if (details && Array.isArray(details)) {
            setError(`${errorMsg}\n\nDetails:\n• ${details.join('\n• ')}`);
          } else {
            setError(errorMsg);
          }
        } else {
          setError(error.response.data?.message || 'An error occurred while saving the package');
        }
      } else if (error.request) {
        console.error('No response received:', error.request);
        setError('Network error. Please check your connection and try again.');
      } else {
        console.error('Error message:', error.message);
        setError(`An unexpected error occurred: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // ListSection component moved outside to prevent re-creation on renders

  return (
    <>
      <Helmet>
        <title>{isEditing ? 'Edit Package' : 'Create Package'} | Bali Getaway Admin</title>
        <meta name="description" content={`${isEditing ? 'Edit an existing' : 'Create a new'} package for Bali Getaway`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 py-8">
        {/* Modern Header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          {/* Navigation */}
          <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/admin/packages')}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg hover:bg-white hover:shadow-md transition-all duration-200"
          >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Packages
          </button>
            
            <div className="hidden md:flex items-center space-x-2 bg-white/60 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20">
              <div className={`w-2 h-2 rounded-full ${isEditing ? 'bg-blue-500' : 'bg-green-500'} animate-pulse`}></div>
              <span className="text-sm font-medium text-gray-700">
                {isEditing ? 'Edit Mode' : 'Create Mode'}
              </span>
            </div>
          </div>

          {/* Page Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl shadow-lg mb-6">
              <SparklesIcon className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent mb-3">
              {isEditing ? 'Edit Package' : 'Create New Package'}
              </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {isEditing
                ? 'Update your package details, pricing, and settings to keep your offerings fresh and appealing'
                : 'Design an amazing travel experience for your customers with our intuitive package builder'
              }
            </p>
          </div>

          {/* Progress Steps - Modern Design */}
          <div className="hidden md:block mb-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-between relative">
                {/* Progress Line */}
                <div className="absolute top-6 left-0 right-0 h-0.5 bg-gray-200"></div>

                {[
                  { id: 'basic', label: 'Basic Info', icon: InformationCircleIcon, desc: 'Name, pricing & details' },
                  { id: 'resorts', label: 'Resorts', icon: BuildingOfficeIcon, desc: 'Select accommodations' },
                  { id: 'images', label: 'Images', icon: CameraIcon, desc: 'Upload photos' },
                  { id: 'details', label: 'Features', icon: DocumentIcon, desc: 'Highlights & inclusions' },
                  { id: 'terms', label: 'Terms', icon: TagIcon, desc: 'Terms & conditions' }
                ].map((step, idx) => {
                  const isActive = true; // You could track current step here
                  const isCompleted = false; // You could track completed steps here

                  return (
                    <div key={step.id} className="relative flex flex-col items-center">
                    <button
                      type="button"
                      onClick={() => document.getElementById(step.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                        className="group relative z-10"
                      >
                        <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-200 ${
                          isActive
                            ? 'bg-gradient-to-r from-amber-500 to-orange-500 border-orange-300 text-white shadow-lg'
                            : isCompleted
                            ? 'bg-green-500 border-green-300 text-white'
                            : 'bg-white border-gray-200 text-gray-400 group-hover:border-amber-300 group-hover:text-amber-600'
                        }`}>
                          <step.icon className="h-6 w-6" />
                      </div>
                    </button>
                      <div className="mt-3 text-center">
                        <div className={`text-sm font-medium ${isActive ? 'text-orange-600' : 'text-gray-500'}`}>
                          {step.label}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {step.desc}
                        </div>
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>
            </div>
          </div>

        {/* Loading Overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm mx-4">
              <div className="flex flex-col items-center">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <SparklesIcon className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">
                  {isEditing ? 'Loading Package' : 'Creating Package'}
                </h3>
                <p className="text-sm text-gray-600 text-center">
                  {isEditing ? 'Please wait while we load your package data...' : 'Setting up your new package...'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information - Modern Card Design */}
            <div id="basic" className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <InformationCircleIcon className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Basic Information</h2>
                    <p className="text-amber-100 text-sm">Essential details for your package</p>
                  </div>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Package Name - Full Width */}
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Package Name <span className="text-red-500">*</span>
                  </label>
                    <div className="relative">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                        className="w-full px-4 py-4 text-lg border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                        placeholder="Enter an attractive package name..."
                  />
                      <div className="absolute right-3 top-4 text-gray-400">
                        <SparklesIcon className="h-5 w-5" />
                      </div>
                    </div>
                </div>

                  {/* Destination Selection - Full Width */}
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Destination <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        name="destination"
                        value={formData.destination}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-4 text-lg border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 bg-gray-50 focus:bg-white appearance-none"
                      >
                        <option value="">Select a destination...</option>
                        {availableDestinations.map((destination) => (
                          <option key={destination._id} value={destination._id}>
                            {destination.name}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-4 text-gray-400 pointer-events-none">
                        <MapPinIcon className="h-5 w-5" />
                      </div>
                    </div>
                    {!formData.destination && (
                      <p className="text-sm text-gray-500 mt-2">
                        Select the destination where this package will be available
                      </p>
                    )}
                  </div>

                  {/* Two Column Grid */}
                  <div className="space-y-6">
                <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-3">
                        Package Code <span className="text-red-500">*</span>
                  </label>
                      <div className="relative">
                  <input
                    type="text"
                    name="packageCode"
                    value={formData.packageCode}
                    onChange={handleInputChange}
                    required
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 bg-gray-50 focus:bg-white uppercase tracking-wider"
                          placeholder="AUTO-GENERATED"
                  />
                        <TagIcon className="h-5 w-5 text-gray-400 absolute right-3 top-3.5" />
                      </div>
                </div>

                <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-3">
                    Max Guests
                  </label>
                  <div className="relative">
                    <UsersIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                    <input
                      type="number"
                      name="maxGuests"
                      value={formData.maxGuests}
                      onChange={handleInputChange}
                      min="1"
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                    />
                  </div>
                </div>

                <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-3">
                        USD Price <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                        <CurrencyDollarIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                    <input
                      type="number"
                          name="priceUSD"
                          value={formData.priceUSD}
                      onChange={handleInputChange}
                      required
                          min="0"
                          step="0.01"
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                          placeholder="0.00"
                    />
                  </div>
                    </div>
                </div>

                  <div className="space-y-6">
                <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-3">
                        Number of Nights <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                        <CalendarIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                    <input
                      type="number"
                          name="nights"
                          value={formData.nights}
                      onChange={handleInputChange}
                          min="1"
                          max="30"
                      required
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                          placeholder="e.g., 3"
                    />
                  </div>
                      <p className="text-xs text-gray-500 mt-2 flex items-center">
                        <ClockIcon className="h-3 w-3 mr-1" />
                        Duration of the package stay
                      </p>
                </div>

                <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-3">
                        AUD Price <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                    <input
                      type="number"
                      name="priceAUD"
                      value={formData.priceAUD}
                      onChange={handleInputChange}
                      required
                      min="0"
                      step="0.01"
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        ⚡ Last Minute Deal USD Price
                        <span className="text-xs text-gray-500 font-normal">(Optional)</span>
                  </label>
                  <div className="relative">
                        <CurrencyDollarIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                    <input
                      type="number"
                          name="lastMinuteDealPriceUSD"
                          value={formData.lastMinuteDealPriceUSD}
                      onChange={handleInputChange}
                          min="0"
                          step="0.01"
                          className="w-full pl-10 pr-4 py-3 border-2 border-red-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 bg-red-50/30 focus:bg-white"
                          placeholder="Special price"
                    />
                  </div>
                    </div>

                <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        ⚡ Last Minute Deal AUD Price
                        <span className="text-xs text-gray-500 font-normal">(Optional)</span>
                  </label>
                  <div className="relative">
                        <CurrencyDollarIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                    <input
                      type="number"
                          name="lastMinuteDealPriceAUD"
                          value={formData.lastMinuteDealPriceAUD}
                      onChange={handleInputChange}
                          min="0"
                          step="0.01"
                          className="w-full pl-10 pr-4 py-3 border-2 border-red-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 bg-red-50/30 focus:bg-white"
                          placeholder="Special price"
                    />
                  </div>
                    </div>

                <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        ⚡ Last Minute Deal Expiry Date
                        <span className="text-xs text-gray-500 font-normal">(Optional)</span>
                  </label>
                  <div className="relative">
                        <CalendarIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                    <input
                      type="date"
                          name="lastMinuteDealExpiryDate"
                          value={formData.lastMinuteDealExpiryDate}
                      onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 border-2 border-red-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 bg-red-50/30 focus:bg-white"
                    />
                  </div>
                      <p className="text-xs text-gray-500 mt-2 flex items-center">
                        <ClockIcon className="h-3 w-3 mr-1" />
                        Set deadline to create urgency
                      </p>
                    </div>

                <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-3">
                        Validity Period (Months) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <CalendarIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                    <input
                      type="number"
                      name="validityPeriod"
                      value={formData.validityPeriod}
                      onChange={handleInputChange}
                      required
                      min="1"
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                    />
                      </div>
                  </div>
                </div>

                  {/* Description - Full Width */}
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                      rows={5}
                      className="w-full px-4 py-4 text-base border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 bg-gray-50 focus:bg-white resize-none"
                      placeholder="Describe your package in detail... What makes it special? What will customers experience?"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Tip: Use engaging language to highlight unique experiences and benefits
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Resort Selection - Modern Card Design */}
            <div id="resorts" className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <BuildingOfficeIcon className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Select Resorts</h2>
                    <p className="text-emerald-100 text-sm">Choose accommodations for your package</p>
                  </div>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-8">
                {/* Destination Filter Info */}
                {formData.destination && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <div className="flex items-center space-x-2">
                      <MapPinIcon className="h-5 w-5 text-blue-600" />
                      <span className="text-blue-800 font-medium">
                        Showing resorts for: {availableDestinations.find(d => d._id === formData.destination)?.name}
                      </span>
                      <span className="text-blue-600 text-sm">({filteredResorts.length} available)</span>
                    </div>
                  </div>
                )}
                
                {!formData.destination ? (
                  <div className="text-center py-16 bg-yellow-50 rounded-2xl border-2 border-dashed border-yellow-200">
                    <MapPinIcon className="h-16 w-16 text-yellow-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">Select a Destination First</h3>
                    <p className="text-gray-600 text-lg">Please choose a destination to see available resorts.</p>
                  </div>
                ) : filteredResorts.length === 0 ? (
                  <div className="text-center py-16 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                    <BuildingOfficeIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No resorts available</h3>
                    <p className="text-gray-500 text-lg">No resorts found for the selected destination.</p>
                    <p className="text-sm text-gray-400 mt-2">Please select a different destination or add resorts to this destination.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredResorts.map((resort) => {
                      const isSelected = formData.resorts.includes(resort._id);
                      return (
                  <div
                    key={resort._id}
                          className={`group relative p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 overflow-hidden hover:shadow-lg ${
                            isSelected
                              ? 'border-emerald-400 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-md ring-2 ring-emerald-200'
                              : 'border-gray-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/30'
                    }`}
                    onClick={() => handleResortSelection(resort._id)}
                  >
                          {/* Background Pattern for Selected */}
                          {isSelected && (
                            <div className="absolute inset-0 opacity-5">
                              <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-teal-400"></div>
                            </div>
                          )}

                          <div className="relative">
                            <div className="flex items-start space-x-4">
                              {/* Resort Image */}
                              <div className={`flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 ${
                                isSelected ? 'border-emerald-300' : 'border-gray-200'
                              }`}>
                                <img
                                  src={resolveImageUrl(resort.bannerImage) || '/api/placeholder/64/64'}
                            alt={resort.name}
                                  className="w-full h-full object-cover"
                          />
                          </div>

                              {/* Resort Info */}
                              <div className="flex-1 min-w-0">
                                <h3 className={`font-bold text-lg mb-1 truncate group-hover:text-emerald-700 transition-colors ${
                                  isSelected ? 'text-emerald-800' : 'text-gray-900'
                                }`}>
                                  {resort.name}
                                </h3>
                                <div className="flex items-center text-sm text-gray-600 mb-2">
                                  <div className={`w-2 h-2 rounded-full mr-2 ${
                                    isSelected ? 'bg-emerald-500' : 'bg-gray-400'
                                  }`}></div>
                                  📍 {resort.destination?.name || 'Unknown Location'}
                        </div>

                                {/* Selection Indicator */}
                                <div className="flex items-center justify-between">
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    isSelected
                                      ? 'bg-emerald-100 text-emerald-800'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {isSelected ? (
                                      <>
                                        <CheckIcon className="h-3 w-3 mr-1" />
                                        Selected
                                      </>
                                    ) : (
                                      'Available'
                                    )}
                                  </span>
                      </div>
                    </div>
                            </div>

                            {/* Hover Effect */}
                            <div className={`absolute inset-0 border-2 rounded-2xl transition-all duration-300 ${
                              isSelected
                                ? 'border-emerald-400 opacity-100'
                                : 'border-transparent opacity-0 group-hover:border-emerald-300 group-hover:opacity-100'
                            }`}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Selection Summary */}
                {formData.resorts.length > 0 && (
                  <div className="mt-8 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <CheckIcon className="h-5 w-5 text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-800">
                          {formData.resorts.length} resort{formData.resorts.length !== 1 ? 's' : ''} selected
                        </span>
                  </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, resorts: [] }))}
                        className="text-sm text-emerald-600 hover:text-emerald-800 font-medium"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Images - Modern Card Design */}
            <div id="images" className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <CameraIcon className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Package Images</h2>
                    <p className="text-purple-100 text-sm">Upload high-quality images to showcase your package</p>
                  </div>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-8 space-y-8">
                {/* Banner Image Section */}
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Banner Image</h3>
                      <p className="text-sm text-gray-600">Main promotional image for your package</p>
                    </div>
                    <span className="px-3 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">Required</span>
                  </div>

                  <div
                    className={`relative border-3 border-dashed rounded-2xl p-8 text-center transition-all duration-300 ${
                      bannerDragActive
                        ? 'border-purple-400 bg-purple-50 scale-[1.02]'
                        : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50/30'
                  }`}
                  onDragOver={onBannerDragOver}
                  onDragLeave={onBannerDragLeave}
                  onDrop={onBannerDrop}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, 'banner')}
                    className="hidden"
                    id="banner-upload"
                  />

                  {previewImages.banner ? (
                    <div className="relative group">
                        <div className="relative overflow-hidden rounded-xl shadow-lg">
                          <img
                            src={previewImages.banner}
                            alt="Banner preview"
                            className="w-full h-64 object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                          {/* Image Info Overlay */}
                          <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                            <div className="flex items-center justify-between">
                              <div className="text-left">
                          {formData.bannerImage ? (
                                  <div>
                                    <p className="font-medium text-sm">{formData.bannerImage.name}</p>
                                    <p className="text-xs opacity-90">{(formData.bannerImage.size / (1024*1024)).toFixed(2)} MB</p>
                                  </div>
                                ) : (
                                  <p className="font-medium text-sm">Current banner</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <label
                            htmlFor="banner-upload"
                                  className="inline-flex items-center px-3 py-1.5 rounded-lg bg-white/20 backdrop-blur-sm text-white text-sm font-medium hover:bg-white/30 transition-colors cursor-pointer"
                          >
                                  <PhotoIcon className="h-4 w-4 mr-2" />
                            Replace
                          </label>
                          {formData.bannerImage && (
                            <button
                              type="button"
                              onClick={removeBannerImage}
                                    className="inline-flex items-center px-3 py-1.5 rounded-lg bg-red-500/80 backdrop-blur-sm text-white text-sm font-medium hover:bg-red-600 transition-colors"
                            >
                                    <TrashIcon className="h-4 w-4 mr-2" />
                              Remove
                            </button>
                          )}
                              </div>
                            </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                      <label htmlFor="banner-upload" className="block cursor-pointer group">
                        <div className="flex flex-col items-center">
                          <div className="w-20 h-20 bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200">
                            <PhotoIcon className="h-10 w-10 text-purple-500" />
                          </div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">Upload Banner Image</h4>
                          <p className="text-gray-600 mb-4">Click to upload or drag & drop your banner image</p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span className="flex items-center">
                              <CheckIcon className="h-4 w-4 mr-1 text-green-500" />
                              PNG, JPG, WEBP
                            </span>
                            <span className="flex items-center">
                              <CheckIcon className="h-4 w-4 mr-1 text-green-500" />
                              Max 5MB
                            </span>
                            <span className="flex items-center">
                              <CheckIcon className="h-4 w-4 mr-1 text-green-500" />
                              1600×600px
                            </span>
                          </div>
                        </div>
                    </label>
                  )}
                </div>
              </div>

                {/* Gallery Images Section */}
              <div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Gallery Images</h3>
                      <p className="text-sm text-gray-600">Additional photos to showcase your package</p>
                    </div>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">Optional</span>
                  </div>

                  {/* Gallery Upload Area */}
                  <div className="border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center hover:border-purple-400 hover:bg-purple-50/30 transition-all duration-200 mb-6">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleImageChange(e, 'gallery')}
                    className="hidden"
                    id="gallery-upload"
                  />
                    <label htmlFor="gallery-upload" className="cursor-pointer group block">
                      <div className="flex flex-col items-center">
                        <div className="w-16 h-16 bg-gradient-to-r from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200">
                          <PlusIcon className="h-8 w-8 text-gray-500" />
                        </div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">Add Gallery Images</h4>
                        <p className="text-gray-600">Upload multiple high-quality images</p>
                      </div>
                  </label>
                </div>
                
                  {/* Gallery Preview Grid */}
                {previewImages.gallery.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <h4 className="font-medium text-gray-900">
                            Gallery Images ({previewImages.gallery.length})
                          </h4>
                          {existingGallery.length > 0 && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                              {existingGallery.length} existing
                            </span>
                          )}
                          {formData.gallery.length > 0 && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                              {formData.gallery.length} new
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-3">
                          {/* Quick Delete Toggle */}
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={quickDeleteMode}
                              onChange={(e) => setQuickDeleteMode(e.target.checked)}
                              className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
                            />
                            <span className="text-sm text-gray-700 font-medium">Quick Delete</span>
                          </label>
                          
                          {/* Remove All Button */}
                          <button
                            type="button"
                            onClick={() => {
                              const confirmed = quickDeleteMode || window.confirm(
                                'Are you sure you want to remove ALL gallery images?\n\nThis action cannot be undone.'
                              );
                              if (confirmed) {
                                setFormData(prev => ({ ...prev, gallery: [] }));
                                setExistingGallery([]);
                                setPreviewImages(prev => ({ ...prev, gallery: [] }));
                                console.log('🗑️ Removed all gallery images');
                              }
                            }}
                            className="text-sm text-red-600 hover:text-red-800 font-medium px-3 py-1 border border-red-300 rounded hover:bg-red-50 transition-colors"
                          >
                            Remove All
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {previewImages.gallery.map((image, index) => (
                          <div key={index} className="relative group">
                            <div className={`aspect-square rounded-xl overflow-hidden border-2 bg-gray-50 ${
                              index < existingGallery.length 
                                ? 'border-blue-300' 
                                : 'border-green-300'
                            }`}>
                              <img
                                src={image}
                                alt={`Gallery ${index + 1}`}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                              />
                            </div>
                            {/* Status Badge */}
                            <div className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-medium ${
                              index < existingGallery.length
                                ? 'bg-blue-500 text-white'
                                : 'bg-green-500 text-white'
                            }`}>
                              {index < existingGallery.length ? 'Existing' : 'New'}
                            </div>
                        {/* Individual Delete Button - Always Visible */}
                        <button
                          type="button"
                          onClick={() => removeGalleryImage(index)}
                          className={`absolute -top-2 -right-2 w-8 h-8 text-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110 z-10 ${
                            quickDeleteMode 
                              ? 'bg-red-600 hover:bg-red-700 ring-2 ring-red-300' 
                              : 'bg-red-500 hover:bg-red-600'
                          }`}
                          title={`${
                            quickDeleteMode ? 'Quick delete' : 'Delete'
                          } ${index < existingGallery.length ? 'existing' : 'new'} image`}
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <p className="text-white text-xs font-medium truncate">Image {index + 1}</p>
                            </div>
                      </div>
                    ))}
                      </div>
                  </div>
                )}
                </div>
              </div>
            </div>

            {/* Details - Modern Card Design */}
            <div id="details" className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <DocumentIcon className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Package Features</h2>
                    <p className="text-blue-100 text-sm">Highlight what makes your package special</p>
                  </div>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column */}
                  <div className="space-y-8">
                    {/* Highlights - Now with Images */}
                    <div>
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-xl flex items-center justify-center">
                          <span className="text-white font-bold">⭐</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">Package Highlights</h3>
                          <p className="text-sm text-gray-600">Key selling points with images for visual appeal</p>
                        </div>
                      </div>

                  {/* Add/Edit Highlight Form */}
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-2xl p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-gray-900">
                        {editingHighlightIndex !== null ? 'Edit Highlight' : 'Add New Highlight'}
                      </h4>
                      {editingHighlightIndex !== null && (
                        <span className="text-sm text-yellow-600 bg-yellow-100 px-3 py-1 rounded-full">
                          Editing #{editingHighlightIndex + 1}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="md:col-span-2">
                        <input
                          type="text"
                          value={newItems.highlight.title}
                          onChange={(e) => setNewItems(prev => ({
                            ...prev,
                            highlight: { ...prev.highlight, title: e.target.value }
                          }))}
                          placeholder="e.g., Private Beach Access, Traditional Spa Experience"
                          className="w-full px-4 py-2 border border-yellow-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          {editingHighlightIndex !== null ? 'Change Image (optional)' : 'Upload Image (optional)'}
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            setNewItems(prev => ({
                              ...prev,
                              highlight: { ...prev.highlight, image: file }
                            }));
                          }}
                          className="w-full px-3 py-2 border border-yellow-300 rounded-xl text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      {editingHighlightIndex !== null ? (
                        <>
                          <button
                            type="button"
                            onClick={saveHighlightEdit}
                            className="flex-1 px-4 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors flex items-center justify-center"
                          >
                            <CheckIcon className="h-5 w-5 mr-2" />
                            Save Changes
                          </button>
                          <button
                            type="button"
                            onClick={cancelHighlightEdit}
                            className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-colors flex items-center justify-center"
                          >
                            <XMarkIcon className="h-5 w-5 mr-2" />
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => addListItem('highlight', 'highlights')}
                          className="w-full px-4 py-2 bg-yellow-500 text-white rounded-xl hover:bg-yellow-600 transition-colors flex items-center justify-center"
                        >
                          <PlusIcon className="h-5 w-5 mr-2" />
                          Add Highlight
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Display Highlights */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {formData.highlights.map((highlight, index) => (
                      <div key={index} className="bg-white border border-yellow-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-start space-x-4">
                          <div className="flex-shrink-0">
                            {highlight.imageUrl || highlight.image ? (
                              <img
                                src={highlight.imageUrl || (highlight.image ? URL.createObjectURL(highlight.image) : '')}
                                alt={highlight.title}
                                className="w-12 h-12 object-cover rounded-lg border border-yellow-300"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-lg flex items-center justify-center">
                                <span className="text-xl text-white">⭐</span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-yellow-900">{highlight.title}</h4>
                            <p className="text-xs text-yellow-600">Package Highlight</p>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button
                              type="button"
                              onClick={() => startEditingHighlight(index)}
                              className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full p-1 transition"
                              title="Edit highlight"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  highlights: prev.highlights.filter((_, i) => i !== index)
                                }));
                              }}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full p-1 transition"
                              title="Delete highlight"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                    </div>

                    {/* Inclusions */}
                    <div>
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-10 h-10 bg-gradient-to-r from-green-400 to-emerald-400 rounded-xl flex items-center justify-center">
                          <span className="text-white font-bold">✅</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">What's Included</h3>
                          <p className="text-sm text-gray-600">Everything customers get with this package</p>
                        </div>
                      </div>
                <ListSection
                        title=""
                  items={formData.inclusions}
                  newItemKey="inclusion"
                  listKey="inclusions"
                        placeholder="e.g., Airport transfers, Daily breakfast, Resort activities"
                        icon=""
                  newItems={newItems}
                  setNewItems={setNewItems}
                  addListItem={addListItem}
                  removeListItem={removeListItem}
                />
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-8">
                    {/* Exclusions */}
                    <div>
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-10 h-10 bg-gradient-to-r from-red-400 to-rose-400 rounded-xl flex items-center justify-center">
                          <span className="text-white font-bold">❌</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">What's Not Included</h3>
                          <p className="text-sm text-gray-600">Items customers need to arrange separately</p>
                        </div>
                      </div>
                <ListSection
                        title=""
                  items={formData.exclusions}
                  newItemKey="exclusion"
                  listKey="exclusions"
                        placeholder="e.g., International flights, Personal expenses, Travel insurance"
                        icon=""
                  newItems={newItems}
                  setNewItems={setNewItems}
                  addListItem={addListItem}
                  removeListItem={removeListItem}
                />
                    </div>

                    {/* Premium Features - Now Simple Text */}
                    <div>
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-10 h-10 bg-gradient-to-r from-purple-400 to-pink-400 rounded-xl flex items-center justify-center">
                          <span className="text-white font-bold">💎</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">Premium Features</h3>
                          <p className="text-sm text-gray-600">Exclusive perks and luxury amenities (text only)</p>
                        </div>
                      </div>
                <ListSection
                        title=""
                  items={formData.premiumFeatures}
                  newItemKey="premiumFeature"
                  listKey="premiumFeatures"
                        placeholder="e.g., Private villa with pool, Premium spa treatments, VIP service"
                        icon=""
                  newItems={newItems}
                  setNewItems={setNewItems}
                  addListItem={addListItem}
                  removeListItem={removeListItem}
                />
                    </div>

                    {/* Features */}
                    <div>
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-10 h-10 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-xl flex items-center justify-center">
                          <span className="text-white font-bold">🏖️</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">Additional Features</h3>
                          <p className="text-sm text-gray-600">Standard amenities and services</p>
                        </div>
                      </div>
                <ListSection
                        title=""
                  items={formData.features}
                  newItemKey="feature"
                  listKey="features"
                        placeholder="e.g., Free WiFi, Pool access, Daily housekeeping"
                        icon=""
                  newItems={newItems}
                  setNewItems={setNewItems}
                  addListItem={addListItem}
                  removeListItem={removeListItem}
                />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Terms & Conditions - Modern Card Design */}
            <div id="terms" className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-slate-500 to-gray-500 p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <TagIcon className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Terms & Conditions</h2>
                    <p className="text-slate-100 text-sm">Important legal and policy information</p>
                  </div>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-8">
                <div className="flex items-center space-x-3 mb-8">
                  <div className="w-10 h-10 bg-gradient-to-r from-slate-400 to-gray-400 rounded-xl flex items-center justify-center">
                    <span className="text-white font-bold">📋</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Terms & Conditions</h3>
                    <p className="text-sm text-gray-600">Legal terms, policies, and important notices</p>
                  </div>
                </div>

              <ListSection
                  title=""
                items={formData.termsAndConditions}
                newItemKey="termCondition"
                listKey="termsAndConditions"
                  placeholder="e.g., Cancellation policy requires 48-hour notice, Minimum age 18 years, No pets allowed"
                  icon=""
                newItems={newItems}
                setNewItems={setNewItems}
                addListItem={addListItem}
                removeListItem={removeListItem}
              />

                {/* Helper Text */}
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <div className="flex items-start space-x-3">
                    <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-semibold text-blue-800 mb-1">Important Legal Note</h4>
                      <p className="text-sm text-blue-700">
                        Terms & conditions are legally binding. Consider consulting with a legal professional to ensure compliance with local regulations.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Package Status & Actions - Modern Card Design */}
            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <SparklesIcon className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Package Status & Actions</h2>
                    <p className="text-orange-100 text-sm">Control visibility and finalize your package</p>
                  </div>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-8">
                {/* Status Toggle */}
                <div className="mb-8">
                  <div className="flex items-center justify-between p-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl border border-gray-200">
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 ${
                        formData.isLive
                          ? 'bg-gradient-to-r from-green-400 to-emerald-400'
                          : 'bg-gradient-to-r from-gray-400 to-slate-400'
                      }`}>
                        {formData.isLive ? (
                          <CheckIcon className="h-6 w-6 text-white" />
                        ) : (
                          <ClockIcon className="h-6 w-6 text-white" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">
                          Package Status
                        </h3>
                        <p className="text-sm text-gray-600">
                          {formData.isLive
                            ? 'This package is live and visible to customers'
                            : 'This package is in draft mode and hidden from customers'
                          }
                        </p>
                      </div>
                    </div>

                    <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isLive}
                      onChange={(e) => setFormData(prev => ({ ...prev, isLive: e.target.checked }))}
                        className="sr-only peer"
                    />
                      <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-green-400 peer-checked:to-emerald-400"></div>
                  </label>
                  </div>

                  {/* Status Messages */}
                  <div className="mt-4">
                    {formData.isLive ? (
                      <div className="flex items-center space-x-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <CheckIcon className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-green-800">Package is Live</p>
                          <p className="text-xs text-green-700">This package will be visible on the public website when saved.</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                        <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                          <ClockIcon className="h-4 w-4 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-amber-800">Draft Mode</p>
                          <p className="text-xs text-amber-700">This package is hidden from customers until you make it live.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-between pt-6 border-t border-gray-200">
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowPreview(true)}
                      className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-indigo-600 shadow-lg hover:shadow-xl transition-all duration-200"
                    >
                      <EyeIcon className="h-5 w-5 mr-2" />
                      Preview Package
                    </button>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => navigate('/admin/packages')}
                      className="px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                      className={`px-8 py-4 font-bold rounded-xl shadow-lg transition-all duration-300 ${
                        loading
                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                          : isEditing
                          ? 'bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white shadow-emerald-200 hover:shadow-emerald-300'
                          : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-amber-200 hover:shadow-amber-300'
                      }`}
                    >
                      {loading ? (
                        <div className="flex flex-col items-center space-y-2">
                          <div className="flex items-center space-x-2">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>{isEditing ? 'Updating...' : 'Creating...'}</span>
                          </div>
                          {uploadProgress > 0 && (
                            <div className="w-full bg-white/20 rounded-full h-2">
                              <div 
                                className="bg-white h-2 rounded-full transition-all duration-300" 
                                style={{ width: `${uploadProgress}%` }}
                              ></div>
                            </div>
                          )}
                          {uploadProgress > 0 && (
                            <span className="text-xs text-white/80">Upload Progress: {uploadProgress}%</span>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <span>{isEditing ? '✅ Update Package' : '🚀 Create Package'}</span>
                        </div>
                      )}
                  </button>
                </div>
              </div>
                </div>
            </div>

            {/* Action Configuration - Modern Card Design */}
            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <SparklesIcon className="h-7 w-7 text-white" />
                </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Action Configuration</h2>
                    <p className="text-indigo-100 text-sm">Configure how customers can interact with your package</p>
                  </div>
                </div>
            </div>

              {/* Card Content */}
              <div className="p-8">
                {/* Primary Action Type */}
                <div className="mb-8">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Primary Action Type</h3>
                  <p className="text-sm text-gray-600 mb-6">Choose the main call-to-action for your package</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <label className={`relative flex cursor-pointer rounded-2xl border-2 p-6 transition-all duration-200 ${
                      formData.actionType === 'enquire'
                        ? 'border-indigo-400 bg-gradient-to-br from-indigo-50 to-purple-50 ring-2 ring-indigo-200'
                        : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/30'
                  }`}>
                    <input
                      type="radio"
                      name="actionType"
                      value="enquire"
                      checked={formData.actionType === 'enquire'}
                      onChange={(e) => setFormData(prev => ({ ...prev, actionType: e.target.value as 'enquire' | 'book' }))}
                      className="sr-only"
                    />
                      <span className="flex flex-1 items-center">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 ${
                          formData.actionType === 'enquire'
                            ? 'bg-gradient-to-r from-indigo-400 to-purple-400'
                            : 'bg-gray-100'
                        }`}>
                          <span className="text-2xl">📋</span>
                        </div>
                        <div className="flex-1">
                          <span className="block text-lg font-semibold text-gray-900 mb-1">Enquire Now</span>
                          <span className="block text-sm text-gray-600">Collect customer details for personalized follow-up</span>
                        </div>
                        {formData.actionType === 'enquire' && (
                          <div className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center">
                            <CheckIcon className="h-4 w-4 text-white" />
                          </div>
                        )}
                    </span>
                  </label>

                    <label className={`relative flex cursor-pointer rounded-2xl border-2 p-6 transition-all duration-200 ${
                      formData.actionType === 'book'
                        ? 'border-emerald-400 bg-gradient-to-br from-emerald-50 to-green-50 ring-2 ring-emerald-200'
                        : 'border-gray-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/30'
                  }`}>
                    <input
                      type="radio"
                      name="actionType"
                      value="book"
                      checked={formData.actionType === 'book'}
                      onChange={(e) => setFormData(prev => ({ ...prev, actionType: e.target.value as 'enquire' | 'book' }))}
                      className="sr-only"
                    />
                      <span className="flex flex-1 items-center">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 ${
                          formData.actionType === 'book'
                            ? 'bg-gradient-to-r from-emerald-400 to-green-400'
                            : 'bg-gray-100'
                        }`}>
                          <span className="text-2xl">🎯</span>
                        </div>
                        <div className="flex-1">
                          <span className="block text-lg font-semibold text-gray-900 mb-1">Book Now</span>
                          <span className="block text-sm text-gray-600">Direct payment processing and instant booking</span>
                        </div>
                        {formData.actionType === 'book' && (
                          <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                            <CheckIcon className="h-4 w-4 text-white" />
                          </div>
                        )}
                    </span>
                  </label>
                </div>
              </div>

              {/* Action Toggles */}
                <div className="mb-8">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Available Actions</h3>
                  <p className="text-sm text-gray-600 mb-6">Enable or disable specific customer actions</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className={`flex items-center justify-between p-6 rounded-2xl border-2 transition-all duration-200 ${
                      formData.enquireEnabled
                        ? 'border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50'
                        : 'border-gray-200 bg-white'
                    }`}>
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          formData.enquireEnabled
                            ? 'bg-gradient-to-r from-indigo-400 to-purple-400'
                            : 'bg-gray-100'
                        }`}>
                          <span className="text-xl">📋</span>
                        </div>
                    <div>
                          <span className="text-sm font-semibold text-gray-900">Enable Enquiries</span>
                      <p className="text-xs text-gray-600">Allow customers to submit enquiry forms</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.enquireEnabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, enquireEnabled: e.target.checked }))}
                      className="sr-only peer"
                    />
                        <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full after:transition-all ${
                          formData.enquireEnabled
                            ? 'bg-gradient-to-r from-indigo-400 to-purple-400 after:bg-white'
                            : 'bg-gray-200 after:bg-white'
                        }`}></div>
                  </label>
                </div>

                    <div className={`flex items-center justify-between p-6 rounded-2xl border-2 transition-all duration-200 ${
                      formData.bookEnabled
                        ? 'border-emerald-200 bg-gradient-to-r from-emerald-50 to-green-50'
                        : 'border-gray-200 bg-white'
                    }`}>
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          formData.bookEnabled
                            ? 'bg-gradient-to-r from-emerald-400 to-green-400'
                            : 'bg-gray-100'
                        }`}>
                          <span className="text-xl">🎯</span>
                        </div>
                    <div>
                          <span className="text-sm font-semibold text-gray-900">Enable Bookings</span>
                      <p className="text-xs text-gray-600">Allow customers to complete payments</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.bookEnabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, bookEnabled: e.target.checked }))}
                      className="sr-only peer"
                    />
                        <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full after:transition-all ${
                          formData.bookEnabled
                            ? 'bg-gradient-to-r from-emerald-400 to-green-400 after:bg-white'
                            : 'bg-gray-200 after:bg-white'
                        }`}></div>
                  </label>
                    </div>
                </div>
              </div>

                {/* Configuration Summary */}
                <div className="p-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl border border-gray-200">
                  <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center">
                    <InformationCircleIcon className="h-5 w-5 mr-2 text-indigo-600" />
                    Current Configuration
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">
                        {formData.actionType === 'enquire' ? '📋' : '🎯'}
                      </span>
                      <div>
                        <p className="font-semibold text-gray-900">Primary Action</p>
                        <p className="text-gray-600">{formData.actionType === 'enquire' ? 'Enquire Now' : 'Book Now'}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">📋</span>
                      <div>
                        <p className="font-semibold text-gray-900">Enquiries</p>
                        <p className={`text-gray-600 ${formData.enquireEnabled ? 'text-green-600' : 'text-red-600'}`}>
                          {formData.enquireEnabled ? '✅ Enabled' : '❌ Disabled'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">💳</span>
                      <div>
                        <p className="font-semibold text-gray-900">Bookings</p>
                        <p className={`text-gray-600 ${formData.bookEnabled ? 'text-green-600' : 'text-red-600'}`}>
                          {formData.bookEnabled ? '✅ Enabled' : '❌ Disabled'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Resort Visibility Settings - Modern Card Design */}
            {formData.resorts.length > 0 && (
              <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
                {/* Card Header */}
                <div className="bg-gradient-to-r from-violet-500 to-purple-500 p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <BuildingOfficeIcon className="h-7 w-7 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">Resort Display Settings</h2>
                      <p className="text-violet-100 text-sm">Control which resorts show the "Explore Resort" button</p>
                    </div>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-8">
                  <p className="text-sm text-gray-600 mb-8">
                    Configure which resorts will display the "Explore Resort" button on your package page for better customer experience.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {formData.resorts.map((resortId) => {
                    const resort = availableResorts.find(r => r._id === resortId);
                    const setting = formData.resortSettings.find(s => s.resortId === resortId);
                    const showExplore = setting ? setting.showExploreOption : true; // Default to true
                    
                    return (
                        <div key={resortId} className={`flex items-center justify-between p-6 rounded-2xl border-2 transition-all duration-200 ${
                          showExplore
                            ? 'border-violet-200 bg-gradient-to-r from-violet-50 to-purple-50'
                            : 'border-gray-200 bg-white'
                        }`}>
                          <div className="flex items-center space-x-4">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                              showExplore
                                ? 'bg-gradient-to-r from-violet-400 to-purple-400'
                                : 'bg-gray-100'
                            }`}>
                              <span className="text-xl">🏨</span>
                            </div>
                          <div>
                              <span className="text-sm font-semibold text-gray-900 block">
                              {resort?.name || 'Resort'}
                            </span>
                            <p className="text-xs text-gray-600">Show "Explore Resort" button</p>
                          </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={showExplore}
                            onChange={(e) => {
                              const newSettings = [...formData.resortSettings];
                              const existingIndex = newSettings.findIndex(s => s.resortId === resortId);
                              
                              if (existingIndex >= 0) {
                                newSettings[existingIndex].showExploreOption = e.target.checked;
                              } else {
                                newSettings.push({
                                  resortId: resortId,
                                  showExploreOption: e.target.checked
                                });
                              }
                              
                              setFormData(prev => ({ ...prev, resortSettings: newSettings }));
                            }}
                            className="sr-only peer"
                          />
                            <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full after:transition-all ${
                              showExplore
                                ? 'bg-gradient-to-r from-violet-400 to-purple-400 after:bg-white'
                                : 'bg-gray-200 after:bg-white'
                            }`}></div>
                        </label>
                      </div>
                    );
                  })}
                  </div>

                  {/* Helper Text */}
                  <div className="mt-8 p-4 bg-violet-50 border border-violet-200 rounded-xl">
                    <div className="flex items-start space-x-3">
                      <InformationCircleIcon className="h-5 w-5 text-violet-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="text-sm font-semibold text-violet-800 mb-1">Display Strategy</h4>
                        <p className="text-sm text-violet-700">
                          Enable "Explore Resort" buttons for resorts you want to highlight. This helps customers discover more about their accommodations.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Global Error Display */}
            {error && (
              <div className="bg-white rounded-3xl shadow-lg border border-red-200 overflow-hidden mb-8">
                <div className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-red-800 mb-2">Error</h3>
                      <p className="text-red-700 mb-4 whitespace-pre-wrap">{error}</p>
                    <button
                      onClick={() => setError('')}
                        className="inline-flex items-center px-4 py-2 bg-red-100 text-red-700 font-medium rounded-lg hover:bg-red-200 transition-colors"
                    >
                        <XMarkIcon className="h-4 w-4 mr-2" />
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
                  </div>
                )}
          </form>
        </div>
      </div>

      {/* Package Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center rounded-t-3xl">
              <h2 className="text-2xl font-bold text-gray-900">📋 Package Preview</h2>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <TrashIcon className="h-6 w-6 text-gray-500" />
              </button>
            </div>
            
            {/* Preview Content */}
            <div className="p-6">
              {/* Header Section */}
              <div className="relative h-64 rounded-2xl overflow-hidden mb-6">
                <img
                  src={previewImages.banner || existingBanner || '/api/placeholder/800/300'}
                  alt={formData.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <h1 className="text-3xl font-bold mb-2">{formData.name || 'Package Name'}</h1>
                  <p className="text-lg opacity-90">{formData.description || 'Package description will appear here...'}</p>
                </div>
                <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-sm font-medium ${
                  formData.isLive 
                    ? 'bg-green-500 text-white' 
                    : 'bg-gray-500 text-white'
                }`}>
                  {formData.isLive ? '🟢 LIVE' : '📝 DRAFT'}
                </div>
              </div>

              {/* Pricing Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200">
                  <div className="text-center">
                    <div className="text-sm text-gray-600 mb-1">💵 USD Price</div>
                    <div className="text-3xl font-bold text-green-600">${formData.priceUSD || '0'}</div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-xl border border-blue-200">
                  <div className="text-center">
                    <div className="text-sm text-gray-600 mb-1">🇦🇺 AUD Price</div>
                    <div className="text-3xl font-bold text-blue-600">${formData.priceAUD || '0'}</div>
                  </div>
                </div>
              </div>

              {/* Package Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                  <h3 className="font-semibold text-gray-900 mb-2">📅 Validity</h3>
                  <p className="text-gray-700">{formData.validityPeriod || '12'} months</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                  <h3 className="font-semibold text-gray-900 mb-2">👥 Max Guests</h3>
                  <p className="text-gray-700">{formData.maxGuests || '2'} guests</p>
                </div>
                <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-200">
                  <h3 className="font-semibold text-gray-900 mb-2">🌙 Duration</h3>
                  <p className="text-gray-700">{formData.nights || '3'} nights</p>
                </div>
              </div>

              {/* Features Lists */}
              <div className="space-y-6">
                {formData.highlights.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">⭐ Package Highlights</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {formData.highlights.map((highlight, index) => (
                        <div key={index} className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-4">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              {highlight.imageUrl || highlight.image ? (
                                <img
                                  src={highlight.imageUrl || (highlight.image ? URL.createObjectURL(highlight.image) : '')}
                                  alt={highlight.title}
                                  className="w-12 h-12 object-cover rounded-lg border border-yellow-300"
                                />
                              ) : (
                                <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-lg flex items-center justify-center">
                                  <span className="text-xl text-white">⭐</span>
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-yellow-900">{highlight.title}</h4>
                              <p className="text-xs text-yellow-600">Package Highlight</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {formData.inclusions.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">✅ What's Included</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {formData.inclusions.map((item, index) => (
                        <div key={index} className="flex items-center text-green-700">
                          <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {formData.exclusions.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">❌ What's Not Included</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {formData.exclusions.map((item, index) => (
                        <div key={index} className="flex items-center text-red-700">
                          <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {formData.premiumFeatures.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">💎 Premium Features</h3>
                    <div className="flex flex-wrap gap-2">
                      {formData.premiumFeatures.map((feature, index) => (
                        <span key={index} className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-900 rounded-full text-sm">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="mt-6 pt-6 border-t border-gray-200 flex gap-3 justify-end">
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  Close Preview
                </button>
                <button
                  onClick={() => {
                    setShowPreview(false);
                    document.querySelector('form')?.requestSubmit();
                  }}
                  className="px-6 py-2 bg-gradient-to-r from-yellow-500 to-orange-600 text-white rounded-xl hover:from-yellow-600 hover:to-orange-700 transition-all"
                >
                  Save Package
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreatePackage;
